import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Copy, Mail, Minus, Plus } from 'lucide-react';
import api, { errorText } from '../services/api';
import { useApi } from '../lib/useApi';
import { limit, num } from '../lib/format';
import { Button, Chip, Field, Input, Modal, PLAN_TONE, Select } from './ui';

interface Preset { key: string; label: string; plan: string; max_vendors: number; max_users: number; max_indicators: number }
const OVERRIDABLE = ['strato', 'meso', 'thermo'];
const TRIAL_DAYS = [14, 30, 60];

interface ProviderCatalog { [key: string]: { label: string; models: string[]; key_hint?: string } }
interface Result {
  tenant_id: string; invite: { link: string | null; email_sent: boolean; error: string | null };
  ai: { configured: boolean; error: string | null };
}

/**
 * Create a workspace for a customer who contacted the FluxWatch team (sales-led). Their admin gets a set-password
 * link: emailed by FluxWatch, and shown here to copy and send yourself.
 */
export default function CreateTenantModal({ onClose, onCreated }: { onClose: () => void; onCreated: (tenantId: string) => void }) {
  const { data: plans } = useApi<{ presets: Preset[]; indicator_total: number }>('/plans');
  const [form, setForm] = useState({
    company_name: '', company_domain: '', admin_name: '', admin_email: '', preset: 'strato',
    plan_status: 'active', trial_days: 30, extra_vendors: 0, extra_users: 0, send_email: true, reason: '',
  });
  const [override, setOverride] = useState(false);
  const [check, setCheck] = useState<{ ok: boolean; problems: string[]; domain: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [copied, setCopied] = useState(false);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(f => ({ ...f, [k]: v }));

  // AI provisioning -- bring-your-own-key (default) or FluxWatch provides it. See
  // core/ai_provisioning.py; FluxWatch itself does the encryption, discovery and connection test.
  const [aiMode, setAiMode] = useState<'byok' | 'platform'>('byok');
  const [catalog, setCatalog] = useState<ProviderCatalog | null>(null);
  const [aiProvider, setAiProvider] = useState('');
  const [aiKey, setAiKey] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [discovered, setDiscovered] = useState<string[] | null>(null);
  const [discovering, setDiscovering] = useState(false);
  useEffect(() => {
    if (aiMode === 'platform' && !catalog) {
      api.get('/ai-provider-catalog').then(r => setCatalog(r.data.providers)).catch(() => setCatalog({}));
    }
  }, [aiMode, catalog]);
  const aiProviderInfo = aiProvider && catalog ? catalog[aiProvider] : null;
  const discoverModels = async () => {
    if (!aiProvider || !aiKey.trim()) return;
    setDiscovering(true); setDiscovered(null);
    try {
      const res = await api.post('/ai-model-discovery', { provider_name: aiProvider, api_key: aiKey.trim() });
      setDiscovered(res.data.models || []);
      if (!aiModel && res.data.models?.length) setAiModel(res.data.models[0]);
    } catch { setDiscovered(aiProviderInfo?.models || []); } finally { setDiscovering(false); }
  };

  // Live check: free mail, an email or domain that's already a customer.
  useEffect(() => {
    if (!form.admin_email.includes('@') && !form.company_domain) { setCheck(null); return; }
    const t = setTimeout(async () => {
      try { setCheck((await api.post('/tenants/check', { admin_email: form.admin_email, company_domain: form.company_domain })).data); }
      catch { setCheck(null); }
    }, 450);
    return () => clearTimeout(t);
  }, [form.admin_email, form.company_domain]);

  const preset = plans?.presets.find(p => p.key === form.preset);
  const canOverride = OVERRIDABLE.includes(form.preset);
  const extraV = canOverride && override ? form.extra_vendors : 0;
  const extraU = canOverride && override ? form.extra_users : 0;
  const ready = form.company_name.trim().length >= 2 && form.admin_name.trim().length >= 2 && form.admin_email.includes('@')
    && form.company_domain.trim().length >= 3 && form.reason.trim().length >= 3 && (!check || check.ok)
    && (aiMode === 'byok' || (aiProvider && aiKey.trim().length > 0));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await api.post('/tenants', {
        ...form, extra_vendors: extraV, extra_users: extraU, reason: form.reason.trim(),
        ai_mode: aiMode, ai_provider_name: aiMode === 'platform' ? aiProvider : null,
        ai_api_key: aiMode === 'platform' ? aiKey.trim() : null, ai_default_model: aiMode === 'platform' ? (aiModel || null) : null,
      });
      setResult(res.data);
      onCreated(res.data.tenant_id);
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  if (result) {
    const inv = result.invite;
    return (
      <Modal open title="Workspace created" onClose={onClose} width="max-w-xl"
        footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div className="text-sm text-slate-700">
            <b className="font-semibold text-slate-900">{form.company_name}</b> is ready on {preset?.label}
            {extraV || extraU ? ` (+${[extraV && `${extraV} vendors`, extraU && `${extraU} users`].filter(Boolean).join(', ')})` : ''}.
            Its first scan of <b className="font-medium">{check?.domain || form.company_domain}</b> starts when {form.admin_name.split(' ')[0]} first signs in.
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Mail className="h-4 w-4 text-slate-500" />Set-password link for {form.admin_email}</div>
          {inv.error ? (
            <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{inv.error} The workspace exists; use <b>Resend invite</b> on its page once FluxWatch is reachable.</span>
            </div>
          ) : (
            <>
              <div className="mt-1 text-xs text-slate-500">
                {inv.email_sent ? 'Emailed to them just now. You can also send it yourself:' : form.send_email
                  ? "The email couldn't be sent (FluxWatch's email service is off). Send this link yourself:" : 'Not emailed, as you chose. Send this link yourself:'}
                {' '}it works once and expires in 7 days.
              </div>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={inv.link || ''} onFocus={e => e.currentTarget.select()} className="font-mono text-xs" />
                <Button icon={copied ? Check : Copy} onClick={() => inv.link && copy(inv.link)}>{copied ? 'Copied' : 'Copy'}</Button>
              </div>
            </>
          )}
        </div>
        {aiMode === 'platform' && (
          result.ai.configured ? (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-purple-100 bg-purple-50 px-4 py-2.5 text-sm text-purple-800">
              <CheckCircle2 className="h-4 w-4" /> AI is provided by FluxWatch ({catalog?.[aiProvider]?.label || aiProvider}{aiModel ? `, ${aiModel}` : ''}).
            </div>
          ) : (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{result.ai.error} The workspace exists on bring-your-own-key for now; set up AI provisioning from its page once FluxWatch is reachable.</span>
            </div>
          )
        )}
      </Modal>
    );
  }

  const stepper = (label: string, key: 'extra_vendors' | 'extra_users', base: number) => (
    <Field label={label} hint={`Plan includes ${num(base)} · total ${num(base + form[key])}`}>
      <div className="flex items-center gap-2">
        <Button size="sm" icon={Minus} onClick={() => set(key, Math.max(0, form[key] - 1))} disabled={form[key] <= 0} aria-label={`Fewer ${label}`} />
        <Input className="!w-20 text-center" type="number" min={0} value={String(form[key])} onChange={e => set(key, Math.max(0, parseInt(e.target.value, 10) || 0))} />
        <Button size="sm" icon={Plus} onClick={() => set(key, form[key] + 1)} aria-label={`More ${label}`} />
      </div>
    </Field>
  );

  return (
    <Modal open title="New tenant" onClose={onClose} width="max-w-2xl"
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} disabled={!ready} onClick={submit}>Create workspace</Button></>}>
      <div className="space-y-5">
        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Company</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Company name"><Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Acme Ltd" autoFocus /></Field>
            <Field label="Company domain" hint={check?.domain && check.domain !== form.company_domain.trim().toLowerCase() ? `Saved as ${check.domain}` : 'Becomes their My Organization'}>
              <Input value={form.company_domain} onChange={e => set('company_domain', e.target.value)} placeholder="acme.com" />
            </Field>
          </div>
        </section>

        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">First admin</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Full name"><Input value={form.admin_name} onChange={e => set('admin_name', e.target.value)} placeholder="Priya Sharma" /></Field>
            <Field label="Work email"><Input type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="priya@acme.com" /></Field>
          </div>
          {check && !check.ok && (
            <div className="mt-2 space-y-1 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {check.problems.map(p => <div key={p} className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{p}</div>)}
            </div>
          )}
        </section>

        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Plan</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {(plans?.presets || []).map(p => (
              <button key={p.key} type="button" onClick={() => set('preset', p.key)}
                className={`rounded-lg border p-2.5 text-left transition ${form.preset === p.key ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
                <Chip tone={PLAN_TONE[p.label] || 'slate'}>{p.label}</Chip>
                <div className="mt-1.5 text-[11px] leading-tight text-slate-500">{limit(p.max_vendors)} vendors<br />{limit(p.max_users)} users</div>
              </button>
            ))}
          </div>
          {canOverride && preset && (
            <div className="mt-3 rounded-lg border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} />
                Override: give extra vendors or users on top of {preset.label}
              </label>
              {override && <div className="mt-3 grid gap-3 sm:grid-cols-2">{stepper('Extra vendors', 'extra_vendors', preset.max_vendors)}{stepper('Extra users', 'extra_users', preset.max_users)}</div>}
            </div>
          )}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Status">
              <Select value={form.plan_status} onChange={e => set('plan_status', e.target.value)}>
                <option value="active">Active (paid)</option><option value="trial">Trial</option>
              </Select>
            </Field>
            {form.plan_status === 'trial' && (
              <Field label="Trial length">
                <Select value={String(form.trial_days)} onChange={e => set('trial_days', parseInt(e.target.value, 10))}>
                  {TRIAL_DAYS.map(d => <option key={d} value={d}>{d} days</option>)}
                </Select>
              </Field>
            )}
          </div>
        </section>

        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">AI</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setAiMode('byok')}
              className={`rounded-lg border p-2.5 text-left transition ${aiMode === 'byok' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="text-sm font-medium text-slate-900">Bring their own key</div>
              <div className="mt-0.5 text-[11px] leading-tight text-slate-500">Default. They configure an AI provider later, from Settings.</div>
            </button>
            <button type="button" onClick={() => setAiMode('platform')}
              className={`rounded-lg border p-2.5 text-left transition ${aiMode === 'platform' ? 'border-purple-500 ring-2 ring-purple-100' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="text-sm font-medium text-slate-900">FluxWatch provides AI</div>
              <div className="mt-0.5 text-[11px] leading-tight text-slate-500">You give the key here. Their Settings &gt; AI Providers page is hidden.</div>
            </button>
          </div>
          {aiMode === 'platform' && catalog && (
            <div className="mt-3 space-y-3 rounded-lg border border-purple-100 bg-purple-50/40 p-3">
              <Field label="Provider">
                <Select value={aiProvider} onChange={e => { setAiProvider(e.target.value); setAiModel(''); setDiscovered(null); }}>
                  <option value="">Select a provider…</option>
                  {Object.entries(catalog).map(([key, info]) => <option key={key} value={key}>{info.label}</option>)}
                </Select>
              </Field>
              {aiProvider && (
                <Field label="API key" hint={aiProviderInfo?.key_hint}>
                  <div className="flex gap-2">
                    <Input type="password" value={aiKey} onChange={e => setAiKey(e.target.value)} placeholder={aiProviderInfo?.key_hint || 'API key'} className="flex-1" />
                    <Button size="sm" loading={discovering} disabled={!aiKey.trim()} onClick={discoverModels}>Discover models</Button>
                  </div>
                </Field>
              )}
              {aiProvider && (discovered || aiProviderInfo?.models?.length) ? (
                <Field label="Default model">
                  <Select value={aiModel} onChange={e => setAiModel(e.target.value)}>
                    <option value="">{discovered ? 'Select a model…' : 'Discover models first, or pick a curated one…'}</option>
                    {(discovered ?? aiProviderInfo?.models ?? []).map(m => <option key={m} value={m}>{m}</option>)}
                  </Select>
                </Field>
              ) : null}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <Field label="Reason / note (kept in plan history and the audit log)" error={err}>
            <Input value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Signed order #1042" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.send_email} onChange={e => set('send_email', e.target.checked)} />
            Email {form.admin_name.split(' ')[0] || 'them'} a welcome message with the set-password link
          </label>
        </section>
      </div>
    </Modal>
  );
}
